import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, Lightbulb, RotateCcw, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ElementLegend, FormulaText, MoleculeDiagram } from './MatterClassifierDiagram';
import {
  ELEMENT_NAMES,
  FORMULA_CASES,
  MATCH_CASES,
  SPECIES,
  analyseFormula,
  checkAtomCount,
  checkElementCount,
  checkMatch,
  checkTotalAtoms,
  layoutMolecule,
  relativeFormulaMass,
  speciesElements,
  type CountCheck,
} from './matterClassifier';

const CARD = 'rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8';
const INPUT = 'mt-1 w-full rounded-2xl border border-hairline bg-surface px-3 py-2 text-base font-extrabold text-ink focus:border-brand-sky focus:outline-none';

function FeedbackLine({ label, check }: { label: string; check: CountCheck | null }) {
  if (!check) {
    return (
      <li className="flex items-start gap-2 text-sm font-bold text-slate2">
        <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-brand-coral" aria-hidden="true" />
        <span>{label}: enter a whole number (0 or more).</span>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-2 text-sm font-medium leading-6 text-ink-soft">
      {check.correct ? (
        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-brand-mint" aria-hidden="true" />
      ) : (
        <Lightbulb className="mt-1 h-4 w-4 shrink-0 text-brand-coral" aria-hidden="true" />
      )}
      <span>
        <strong className="text-ink">{label} — {check.correct ? 'correct' : 'not yet'}.</strong> {check.feedback}
      </span>
    </li>
  );
}

function toWholeNumber(value: string): number {
  return value.trim() === '' ? Number.NaN : Number(value);
}

function CaseSelect({ label, value, options, onChange }: { label: string; value: string; options: Array<{ id: string; text: string }>; onChange: (id: string) => void }) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-2xl border border-hairline bg-surface px-3 py-2 text-sm font-extrabold text-ink sm:w-auto"
    >
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.text}
        </option>
      ))}
    </select>
  );
}

export function FormulaCountPanel() {
  const [caseId, setCaseId] = useState(FORMULA_CASES[0].id);
  const item = FORMULA_CASES.find((candidate) => candidate.id === caseId) ?? FORMULA_CASES[0];
  const analysis = useMemo(() => analyseFormula(item.formula).analysis, [item.formula]);
  const [elementsInput, setElementsInput] = useState('');
  const [totalInput, setTotalInput] = useState('');
  const [countsChecked, setCountsChecked] = useState(false);
  const [perElement, setPerElement] = useState<Record<string, string>>({});
  const [elementsChecked, setElementsChecked] = useState(false);
  useScienceTutorSection('FormulaCountPanel', { caseId, elementsInput, totalInput, countsChecked, perElement, elementsChecked });

  const reset = (id = caseId) => {
    setCaseId(id);
    setElementsInput('');
    setTotalInput('');
    setCountsChecked(false);
    setPerElement({});
    setElementsChecked(false);
  };

  if (!analysis) return null;

  return (
    <section className={CARD} aria-labelledby="count-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Read a formula</p>
      <h2 id="count-heading" className="mt-2 text-2xl font-extrabold text-ink">Count the atoms</h2>
      <p className="mt-1 text-sm font-medium leading-6 text-slate2">
        A subscript counts the atom just before it. A number after a closing bracket multiplies everything inside the brackets.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <CaseSelect
          label="Choose a formula"
          value={caseId}
          options={FORMULA_CASES.map((candidate, index) => ({ id: candidate.id, text: `${index + 1}. ${candidate.name}` }))}
          onChange={(id) => reset(id)}
        />
        <button type="button" onClick={() => reset()} className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink hover:bg-surface">
          <RotateCcw className="h-4 w-4" aria-hidden="true" /> Start again
        </button>
      </div>
      <p className="mt-5 text-4xl font-extrabold tracking-tight text-ink" aria-label={`Formula ${item.formula}`}>
        <FormulaText formula={item.formula} />
      </p>
      <p className="mt-1 text-sm font-bold text-slate2">{item.name}</p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <label className="text-sm font-extrabold text-ink">
          Different elements
          <input type="number" min="0" step="1" inputMode="numeric" value={elementsInput} disabled={countsChecked} onChange={(event) => setElementsInput(event.target.value)} className={INPUT} />
        </label>
        <label className="text-sm font-extrabold text-ink">
          Total atoms in one formula unit
          <input type="number" min="0" step="1" inputMode="numeric" value={totalInput} disabled={countsChecked} onChange={(event) => setTotalInput(event.target.value)} className={INPUT} />
        </label>
      </div>
      {!countsChecked ? (
        <button
          type="button"
          disabled={!elementsInput || !totalInput}
          onClick={() => setCountsChecked(true)}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Sparkles className="h-4 w-4" aria-hidden="true" /> Check counts
        </button>
      ) : (
        <ul className="mt-4 space-y-2 rounded-[1.5rem] border border-hairline bg-surface p-4" role="status" aria-label="Count feedback">
          <FeedbackLine label="Different elements" check={checkElementCount(item.formula, toWholeNumber(elementsInput))} />
          <FeedbackLine label="Total atoms" check={checkTotalAtoms(item.formula, toWholeNumber(totalInput))} />
        </ul>
      )}

      {countsChecked ? (
        <div className="mt-6 border-t border-hairline pt-5">
          <h3 className="text-lg font-extrabold text-ink">Now count each element</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {analysis.elements.map((element) => (
              <label key={element} className="text-sm font-extrabold text-ink">
                {ELEMENT_NAMES[element] ?? element} ({element})
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={perElement[element] ?? ''}
                  disabled={elementsChecked}
                  onChange={(event) => setPerElement((current) => ({ ...current, [element]: event.target.value }))}
                  className={INPUT}
                />
              </label>
            ))}
          </div>
          {!elementsChecked ? (
            <button
              type="button"
              disabled={analysis.elements.some((element) => !perElement[element])}
              onClick={() => setElementsChecked(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Check each element
            </button>
          ) : (
            <>
              <ul className="mt-4 space-y-2 rounded-[1.5rem] border border-hairline bg-surface p-4" role="status" aria-label="Element count feedback">
                {analysis.elements.map((element) => (
                  <FeedbackLine key={element} label={ELEMENT_NAMES[element] ?? element} check={checkAtomCount(item.formula, element, toWholeNumber(perElement[element] ?? ''))} />
                ))}
              </ul>
              <div className="mt-4 flex flex-wrap gap-2" aria-label="Model atom counts">
                {analysis.elements.map((element) => (
                  <span key={element} className="rounded-2xl border border-brand-mint/40 bg-wash-mint px-4 py-2 text-sm font-extrabold text-ink">
                    {element} × {analysis.counts[element]}
                  </span>
                ))}
                <span className="rounded-2xl border border-hairline bg-canvas-pure px-4 py-2 text-sm font-extrabold text-ink">
                  {analysis.elementCount} elements · {analysis.totalAtoms} atoms
                </span>
              </div>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}

export function MatchPanel() {
  const [caseId, setCaseId] = useState(MATCH_CASES[0].id);
  const [choice, setChoice] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('MatchPanel', { caseId, choice, checked });
  const item = MATCH_CASES.find((candidate) => candidate.id === caseId) ?? MATCH_CASES[0];
  const species = SPECIES[item.speciesId];
  const layout = useMemo(() => layoutMolecule(species), [species]);
  const result = checked && choice ? checkMatch(item, choice) : null;

  const choose = (id: string) => {
    setCaseId(id);
    setChoice(null);
    setChecked(false);
  };

  return (
    <section className={CARD} aria-labelledby="match-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Ball-and-stick</p>
      <h2 id="match-heading" className="mt-2 text-2xl font-extrabold text-ink">Match the model to its formula</h2>
      <div className="mt-4">
        <CaseSelect
          label="Choose a model"
          value={caseId}
          options={MATCH_CASES.map((candidate, index) => ({ id: candidate.id, text: `Model ${index + 1}` }))}
          onChange={choose}
        />
      </div>
      <div className="mt-4 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <MoleculeDiagram atoms={layout.atoms} bonds={layout.bonds} atomRadius={11} label={`Ball-and-stick model with ${species.atoms.length} atoms`} />
          <div className="mt-3">
            <ElementLegend elements={speciesElements(species)} />
          </div>
          <p className="mt-2 text-xs font-medium text-slate2">Drawn flat. Lines show bonds; two or three lines show double or triple bonds.</p>
        </div>
        <div className="min-w-0">
          <div className="grid grid-cols-2 gap-2" aria-label="Formula options">
            {item.options.map((formula) => (
              <button
                key={formula}
                type="button"
                disabled={checked}
                aria-pressed={choice === formula}
                aria-label={formula}
                onClick={() => setChoice(formula)}
                className={`rounded-2xl border px-4 py-3 text-xl font-extrabold transition-colors disabled:cursor-default ${
                  choice === formula ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-surface text-ink-soft hover:border-brand-sky/40'
                }`}
              >
                <FormulaText formula={formula} />
              </button>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!choice || checked}
              onClick={() => setChecked(true)}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Check match
            </button>
            <button type="button" onClick={() => choose(caseId)} className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface">
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Try again
            </button>
          </div>
          {result ? (
            <div
              role="status"
              aria-label="Match feedback"
              className={`mt-4 rounded-[1.5rem] border p-4 ${result.correct ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'}`}
            >
              <p className="font-extrabold text-ink">
                {result.correct ? 'Matches the model' : 'Not this formula'} — the model is <FormulaText formula={result.expected} />
              </p>
              <p className="mt-1 text-sm font-medium leading-6 text-slate2">{result.feedback}</p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export function FreeFormulaPanel() {
  const [input, setInput] = useState('Ca(OH)2');
  useScienceTutorSection('FreeFormulaPanel', { input });
  const { analysis, error } = analyseFormula(input);
  const formulaMass = analysis ? relativeFormulaMass(analysis) : null;
  return (
    <section className={CARD} aria-labelledby="free-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">Try your own</p>
      <h2 id="free-heading" className="mt-2 text-xl font-extrabold text-ink">Read any simple formula</h2>
      <label className="mt-3 block text-sm font-extrabold text-ink">
        Formula (type digits normally, for example Al2(SO4)3)
        <input value={input} onChange={(event) => setInput(event.target.value)} spellCheck={false} autoCapitalize="off" className={INPUT} />
      </label>
      <div className="mt-3" aria-live="polite">
        {analysis ? (
          <p className="text-sm font-bold leading-6 text-ink">
            <FormulaText formula={analysis.formula} />: {analysis.elements.map((element) => `${element} × ${analysis.counts[element]}`).join(', ')} — {analysis.elementCount} elements, {analysis.totalAtoms} atoms.
          </p>
        ) : (
          <p className="text-sm font-bold leading-6 text-ink-soft">Cannot read this formula: {error}</p>
        )}
        {formulaMass?.mass ? (
          <p className="mt-2 rounded-2xl bg-wash-coral px-3 py-2 font-mono text-sm font-bold text-ink" data-testid="formula-mass-working">
            {formulaMass.mass.working}
          </p>
        ) : null}
        {formulaMass?.error ? (
          <p className="mt-2 text-sm font-bold leading-6 text-ink-soft" data-testid="formula-mass-working">
            {formulaMass.error}
          </p>
        ) : null}
        {analysis ? (
          <p className="mt-1 text-xs font-semibold text-slate2">
            Ar values are standard atomic weights from the periodic-table data. Mr has no unit.
          </p>
        ) : null}
      </div>
    </section>
  );
}
